import dateFormat from 'dateformat';
import Enum from 'enum'; // see https://www.npmjs.com/package/enum

//
// Keep in sync with backend utils.
//
const MailOptions = new Enum({ 'ANY': 0, 'ORDER_UPDATES': 1, 'NONE': 2 });

//
// Keep in sync with backend utils.
//
const Units = new Enum({ 'in': 'inch', 'mm': 'millimetre', 'cm': 'centimetre', 'm': 'metre', 'g': 'gram', 'kg': 'kilogram', 'lb': 'pound' });

//
// Keep in sync with server
// version.
//
const LogLevels = new Enum({ 'NONE': 0, 'INFO': 10, 'CONFIG': 20, 'DEBUG': 30 });

//
// Check to see if a value is an object or array, i.e. non-simple.
// See https://stackoverflow.com/questions/11182924/how-to-check-if-javascript-object-is-json
// Also https://www.w3schools.com/js/js_typeof.asp
//
function isComplex(o) {
    if (o === null) {
        return false;
    }
    if (o === undefined) {
        return false;
    }
    if (typeof o === "string") {
        return false;
    }
    if (typeof o === "boolean") {
        return false;
    }
    if (typeof o === "number") {
        return false;
    }
    if (typeof o === "bigint") { // bigint
        return false;
    }
    if (typeof o === "symbol") {
        return false;
    }
    if (o.constructor === ({}).constructor) {
        return true;
    }
    if (o.constructor === [].constructor) {
        return true;
    }
    throw Error('unknown type -> ' + JSON.stringify(o));
}

function isArray(o) {
    if (!isComplex(o) || isObject(o)) return false;
    return o.constructor === [].constructor;
}

function isObject(o) {
    if (!isComplex(o) || isArray(o)) return false;
    return o.constructor === ({}).constructor;
}

//
// Convert Phone~Case+Recyclable@Gelato => Phone Case | Recyclable
//      Poster+Giclée~Print@Gelato => Poster | Giclée Print
//      Framed~Poster+Natural@Gelato => Framed Poster | Natural
//      Canvas+Slim@Gelato => Canvas | Slim
//      Poster@Gelato => Poster
//      Wood~Print@Gelato => Wood Print
//
function variantToFormat(variant) {
    const regex = /([a-z\éA-Z~\+]+)[\@]{1}([a-zA-Z])/;
    const comps = variant.match(regex);

    if (comps.length !== 3) {
        throw Error('invalid variant -> ' + variant);
    }

    const specComps = comps[1].split('+');

    let leftPart = '';
    let rightPart = '';
    
    if (specComps.length > 1) {
        leftPart += camelize(specComps[0], true, false); 
        rightPart = camelize(specComps[1], true, false); 
    } else {
        rightPart = camelize(specComps[0], true, false);
    }
    
    return `${leftPart.length ? `${leftPart} | ` : ``}${rightPart}`;
}

//
// Convert iPhone_16_Pro.phone~case+recyclable.gto => iPhone 16 Pro Case | Recyclable, dropping the duplicate Phone part
//      12x18_in.poster+giclée~print.gto => Poster | Giclée Print
//      12x18_in.framed~poster+natural.gto => Framed Poster | Natural
//      12x18_in.canvas+slim.gto => Canvas | Slim
//      12x18_in.poster.gto => Poster
//      12x18_in.wood~print.gto => Wood Print
//
function specToOptionFull(spec, type) {
    const regex = /([a-zA-Z0-9_]+)\.([a-zA-Z~]+[\+]*[a-z\éA-Z~]+)\.([a-z]{3})/;
    const comps = spec.match(regex); // [iPhone_16_Pro.phone~case+recyclable.gto, iPhone_16_Pro, phone~case+recyclable, gto]

    if (comps.length !== 4) {
        throw Error(`invalid spec (type ${type}) -> ${spec}`);
    }

    let leftPart = '';

    if (type === 'model') {
        leftPart = comps[1].split('_').reduce((accum, curr) => { return accum + ' ' + curr }); // iPhone 16 Pro
    }

    const specComps = comps[2].split('+'); // [phone~case, recyclable]

    let rightPart = '';

    if (type === 'model') {
        const specWords = camelize(specComps[0], true, false).split(' ');
        specWords.forEach(word => {
            if (leftPart.toLowerCase().indexOf(word.toLowerCase()) === -1) {
                leftPart += `${leftPart.length ? ` ` : ``}${word}`; // iPhone 16 Pro Case
            }
        })
        rightPart = specComps.length > 1 ? camelize(specComps[1], true, false) : ''; // Recyclable
    } else if (type === 'size' && specComps.length > 1) {
        leftPart += camelize(specComps[0], true, false); // Poster
        rightPart = camelize(specComps[1], true, false); // Frame Ready
    } else if (type === 'size') {
        rightPart = camelize(specComps[0], true, false); // Wood Print or Poster
    }

    return `${leftPart.length ? `${leftPart} | ` : ``}${rightPart}`;
}

function specToOption(spec, type) {
    const regex = /([a-zA-Z0-9_]+)\.([a-zA-Z~]+[\+]*[a-z\éA-Z~]+)\.([a-z]{3})/; // escape é in giclée
    const comps = spec.match(regex); // [iPhone_16_Pro.phone~case+recyclable.gto, iPhone_16_Pro, phone~case+recyclable, gto]
    if (type === 'size') {
        return comps[1].split('_')[0]; // 12x18
    } else if (type === 'model') {
        return comps[1].split('_').reduce((accum, curr) => { return accum + ' ' + curr }); // iPhone 16 Pro
    } else {
        throw Error('unhandled type -> ' + type);
    }
}

//
// Convert poster or POSTER to Poster and about-us, about_us to About Us.
// If keepSpaces is false, collapse phone~case to PhoneCase.
// Preserves multi spaces in a string, so 'Record   Number' returns as is.
// Keep in sync with /backend/utils.js.
//
function camelize(text, keepSpaces, keepDelims) {

    let comps = [];
    let delim = '';

    if (text.indexOf('-') !== -1) { // e.g. about-us
        delim = '-';
        comps = text.split('-');
    } else if (text.indexOf('_') !== -1) {
        delim = '_';
        comps = text.split('_');
    } else if (text.indexOf('~') !== -1) {
        delim = '~';
        comps = text.split('~');
    } else {
        let truncText = text.trim();
        comps = truncText.split(' ');
    }

    let camelComps = '';

    for (let i = 0; i < comps.length; i++) {
        if (camelComps.length > 0) {
            if (keepDelims) {
                camelComps = camelComps + delim;
            } else if (keepSpaces) {
                camelComps = camelComps + ' ';
            }
        }
        let comp = comps[i];
        camelComps = camelComps + comp.substring(0, 1).toUpperCase() + comp.substring(1).toLowerCase();
    }

    return camelComps;
}

// 
// Convert someFunction to Some Function
// and someBiggerFunction to Some Bigger Function.
//
function camelizeFunctionText(text) {
    let comps = [];
    let comp = '';
    for (let i = 0; i < text.length; i++) {
        const c = text.charAt(i);
        if (c.toLowerCase() !== c) { // F
            comps.push(comp);
            comp = '';
            comp += c;
        } else if (i === text.length - 1) { // n
            comp += c;
            comps.push(comp);
        } else if (comp.length === 0) { // s or F; new comp
            comp += c.toUpperCase();
        } else {
            comp += c;
        }
    }
    let camel = '';
    comps.forEach(c => {
        if (camel.length > 0) {
            camel += ' ';
        }
        camel += c
    });
    return camel;
}

//
// Convert 'get mojo for adventure' into Get Mojo For Adventure.
//
function titelize(text) {
    const words = text.split(' ');
    let newText = '';
    words.forEach(word => {
        const first = word.substring(0, 1).trim();
        const tail = word.substring(1, word.length).trim();
        if (newText.length) {
            newText += ' ' + first.toUpperCase() + tail.toLowerCase(); // Adventure
        } else {
            newText += first.toUpperCase() + tail.toLowerCase();
        }
    });
    return newText;
}

//
// Concat elements in array
// with a space after delimiter.
//
function stringify(array, delimiter, lowerCase) {
    let text = '';
    array.forEach(entry => {
        if (text.length) {
            text += lowerCase ? `${delimiter} ${entry}`.toLowerCase() : `${delimiter} ${entry}`;
        } else {
            text += lowerCase ? entry.toLowerCase() : entry;
        }
    });
    return text;
}

//
// Convert from storage.
//
function dec(t) {
    let out = '';

    for (let i = 0; i < t.length; i += 2) {
        if (i % 2 === 0) {
            const numChars = t[i];
            const chars = t[i + 1];
            for (let j = 0; j < numChars; j++) {
                out += chars;
            }
        }
    }

    let u = '';

    for (let i = out.length - 1; i >= 0; i--) {
        u += out[i];
    }

    return u;
}

//
// Convert for storage.
//
function enc(t) {
    let f = '';

    for (let i = t.length - 1; i >= 0; i--) {
        f += t[i];
    }

    let o = '';
    let count = 0;

    for (let i = 0; i < f.length; i++) {
        ++count;
        if (f[i + 1] !== f[i]) {
            o += `${count}${f[i]}`;
            count = 0;
            continue;
        }
    }

    return o;
}

//
// Build a string to reflect to customer on PDF confirmation
// in their local time (minus the GMT part) to match the time they placed the order.
// Not designed to parse directly with Date. Doing it this way
// because instantiating Date goes back to local time (so doesn't work if done on server).
// e.g. Sat, Jun 01, 2024 @ 09:13 GMT+0200 (South Africa Standard Time)
// Keep in sync with /backend/utils.formatLongDate.
//
function formatCheckoutDate(millisDate) {
    const date = new Date(millisDate);
    let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const pad = (number) => {
        return number <= 9 ? '0' + number : number;
    }
    const hour = pad(date.getHours());
    const minute = pad(date.getMinutes());
    const timePart = `${hour}:${minute}`;
    const weekDay = date.getDay(); // 0 is Sunday
    const monthDay = pad(date.getDate()); // months start at 1
    const month = date.getMonth();
    const year = date.getFullYear();
    const longLocal = date.toString(); // Thu May 30 2024 07:11:27 GMT+0200 (South Africa Standard Time)
    const zonePart = longLocal.split('GMT')[1]; // +0200 (South Africa Standard Time)
    return `${days[weekDay]}, ${months[month]} ${monthDay}, ${year} @ ${timePart} GMT${zonePart}`; // Sat, Jun 01, 2024 @ 09:13 GMT+0200 (South Africa Standard Time)
}

//
// Format to 2024-05-31T07:11:27+0200 for storage.
// Parses back directly with new Date('2024-05-31 07:11:27+0200')
// (always to local time, i.e. server time if instantiated on the server) 
// e.g. Thu May 30 2024 07:11:27TGMT+0200 (South Africa Standard Time).
// Keep in sync with backend/utils version.
//
function formatDbDate(millisDate) {
    const date = new Date(millisDate);
    return dateFormat(date, 'isoDateTime'); // 2024-05-31T07:11:27+0200
}

//
// 00:15 or 00:09.
//
function formatTime(date, addSeconds) {
    const pad = (number) => {
        return number <= 9 ? '0' + number : number;
    }
    const hours = pad(date.getHours());
    const mins = pad(date.getMinutes());
    const secs = pad(date.getSeconds());
    return addSeconds ? `${hours}:${mins}:${secs}` : `${hours}:${mins}`;
}

//
// Sat 04 Jan 2025.
//
function formatShortDate(date) {
    const pad = (number) => {
        return number <= 9 ? '0' + number : number;
    }
    let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${pad(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function formatMoney(price, currency) {
    return price.toLocaleString(currency.countryCode, {
        style: 'currency',
        currency: currency.currencyCode,
    });
}

function formatMoneyDefault(price, currencyCode, countryCode) {
    return price.toLocaleString(countryCode, {
        style: 'currency',
        currency: currencyCode,
    });
}

function currencySymbol(currencyCode, countryCode) {
    const formatter = Intl.NumberFormat(countryCode, {
        style: 'currency',
        currency: currencyCode,
    });
    let symbol;
    formatter.formatToParts(0).forEach(({ type, value }) => {
        if (type === 'currency') {
            symbol = value;
            return;
        }
    });
    return symbol;
}

//
// See https://stackoverflow.com/questions/9353791/strict-html-parsing-in-javascript
//
function htmlChecks(html) {
    var parser = new DOMParser(), d = parser.parseFromString('<?xml version="1.0"?>' + html, 'text/xml'), allnodes;
    if (d.querySelector('parsererror')) { // HTML (XML) not well formed
        return false;
    } else {
        d = parser.parseFromString(html, 'text/html');
        allnodes = d.getElementsByTagName('*');
        for (var i = allnodes.length - 1; i >= 0; i--) {
            if (allnodes[i] instanceof HTMLUnknownElement) return false;
        }
    }
    return true; // syntax good, all tags closed
}

export {
    camelize, camelizeFunctionText, titelize, stringify, Units, MailOptions,
    formatCheckoutDate, dec, enc, formatDbDate, formatShortDate, formatTime, formatMoney,
    formatMoneyDefault, currencySymbol, htmlChecks, LogLevels, isComplex, isArray, isObject, specToOption, variantToFormat
};